(ns frontend.view.modals.decisions.view
  (:require
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.decisions.event :as event]
   [frontend.view.modals.decisions.sub :as sub]
   [oops.core :as oops]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.combo-box.view :as combo-box]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]))

(def modal-key ::key)

(def ^:private text-like-data-types
  #{:data.type/text-simple :data.type/text :data.type/text-multiline
    :data.type/email :data.type/text-url})

(defn data-field
  "Fixed-editor-type decision field on the new data-input, plus the external
   error-text line. `on-save!` is (fn [attribute value]). Text/textarea fields
   commit on blur (matching the old field-editor); selects commit on change."
  [{:keys [editor-type data-type attribute value options placeholder
           menu-portal-target on-save! error-text]}]
  (let [text-like?  (contains? text-like-data-types data-type)
        multiline?  (= data-type :data.type/text-multiline)]
    [:<>
     [data-input/view
      (cond-> {:data-type   data-type
               :full-width? true
               :value       value
               :placeholder placeholder
               :error       error-text}
        editor-type        (assoc :editor-type editor-type)
        (seq options)      (assoc :options options)
        menu-portal-target (assoc :menu-portal-target menu-portal-target)
        ;; keep textareas in the dense decision tables the same height as the
        ;; sibling single-line inputs (override the component's default min-h-28)
        multiline?         (assoc :class "min-h-10")
        text-like?         (assoc :on-blur (fn [new-value]
                                             (when (not= new-value value)
                                               (on-save! attribute new-value))))
        (not text-like?)   (assoc :on-change (fn [new-value & _]
                                               (on-save! attribute new-value))))]
     (when error-text
       [:p
        {:class "text-danger-light-text"}
        error-text])]))

(defn participant-role-field
  "Role multi-select against `:decision-participant/role` named-values, with
   create-on-fly (type a new role → opens the named-value creator). `on-save!`
   is (fn [role-ids])."
  [{:keys [role-ids error-text on-save!]}]
  (let [value-opts (->> role-ids
                        (keep (fn [id]
                                (when-let [e (session/get-entity id)]
                                  {:value id
                                   :label (entity/display-string e)})))
                        (vec))]
    [:<>
     [data-input/view
      {:editor-type               :editor.type/search
       :data-type                 :data.type/ref-multi
       :full-width?               true
       :named-value-attribute     :decision-participant/role
       :field/entity-types        [:entity.type/named-value]
       :field/select-entity-types [:entity.type/named-value]
       :field/create-entity-types [:entity.type/named-value]
       :value                     value-opts
       :error                     error-text
       :on-change                 (fn [values & _]
                                    (on-save! (vec values)))
       :create!                   (fn [input-str]
                                    (f-entity/open-creator!
                                     [:entity.type/named-value]
                                     {:attribute             :decision-participant/role
                                      :named-value-attribute :decision-participant/role
                                      :start-value           input-str
                                      :skip-success-msg?     true
                                      :on-ok!                (fn [{:keys [new-entity-id close-cb!]}]
                                                               (when (number? new-entity-id)
                                                                 (on-save! (conj (vec role-ids) new-entity-id)))
                                                               (when (fn? close-cb!) (close-cb!)))}))}]
     (when error-text
       [:p
        {:class "text-danger-light-text"}
        error-text])]))

(defn schema-field
  "Schema-driven decision value field: derives editor-type / data-type / options
   from the merged field-schema and renders the new data-input + error line.
   Text/textarea commit on blur, everything else on change. `on-save!` is
   (fn [value]). Multi/ref values are normalized to db ids.

   Option-fields keep create-on-fly, gated on option-create access. Callers pass
   `entity-type` explicitly since `containing-entity` may be a sub-entity."
  [{:keys [value placeholder menu-portal-target error-text on-save! containing-entity
           entity-type named-value-attribute]
    :as   m}]
  (let [data-type   (:field/data-type m)
        editor-type (let [e (or (:editor/type m)
                                (->> m :field/default-visibility :editor))]
                      (when-not (= e :editor.type/none) e))
        select?     (some #{editor-type} [:editor.type/select :editor.type/multi-select])
        text-like?  (contains? text-like-data-types data-type)
        multiline?  (= data-type :data.type/text-multiline)
        multi?      (contains? #{:data.type/ref-multi :data.type/keyword-multi} data-type)
        options     (when select?
                      (fu/field-schema-options m {:containing-entity containing-entity}))
        norm-value  (if multi?
                      (mapv (fn [v] (if (map? v) (:db/id v) v)) value)
                      value)
        attribute   (or (:attribute m)
                        (:field/attribute m))
        can-create? (and (:create-on-fly? m)
                         named-value-attribute
                         entity-type
                         attribute
                         (f-permissions/field-option-create-access? attribute
                                                                    entity-type
                                                                    nil))]
    [:<>
     [data-input/view
      (cond-> {:data-type   data-type
               :full-width? true
               :value       norm-value
               :placeholder placeholder
               :error       error-text}
        editor-type        (assoc :editor-type editor-type)
        (seq options)      (assoc :options options)
        can-create?        (assoc :create!
                                  (fn [input-str]
                                    (f-entity/open-creator!
                                     [:entity.type/named-value]
                                     {:attribute             attribute
                                      :named-value-attribute named-value-attribute
                                      :start-value           input-str
                                      :skip-success-msg?     true
                                      :on-ok!                (fn [{:keys [new-entity-id close-cb!]}]
                                                               (when (number? new-entity-id)
                                                                 (on-save! (if multi?
                                                                             (-> (or norm-value [])
                                                                                 (conj new-entity-id)
                                                                                 (vec))
                                                                             new-entity-id)))
                                                               (when (fn? close-cb!)
                                                                 (close-cb!)))})))
        menu-portal-target (assoc :menu-portal-target menu-portal-target)
        ;; match the row's single-line inputs (override the textarea's min-h-28)
        multiline?         (assoc :class "min-h-10")
        text-like?         (assoc :on-blur (fn [new-value]
                                             (when (not= new-value value)
                                               (on-save! new-value))))
        (not text-like?)   (assoc :on-change (fn [new-value & _]
                                               (on-save! new-value))))]
     (when error-text
       [:p
        {:class "text-danger-light-text"}
        error-text])]))

(defn decision-participant-editor
  []
  (fn [{:keys [decision entities update-entity-field validation-errors tenant-schema]}]
    (let [participants (->> decision
                            :decision/participants
                            (map (partial get entities)))
          participant-persons
          (->> participants
               (map (fn [{:keys [decision-participant/person]}]
                      person))
               (set))]
      [:div
       {:class "p-4 flex flex-col gap-3 border border-solid border-base-light-border-paler rounded"}
       [:h4
        {:class "pb-2 font-bold text-xl"}
        (loc/tr :decision-participants)]
       [:div
        [:div
         {:class "flex gap-3"}
         (when (session/contact-lists?)
           [combo-box/view
            {:on-change                (fn [contact-list-id]
                                         (let [members (->> (session/get-entity contact-list-id)
                                                            :contact-list/members
                                                            (vec))]
                                           (rf/dispatch [::event/add-decision-participants members])))
             :options                  (->> @session/contact-lists
                                            (mapv (fn [entity]
                                                    {:value (:db/id entity)
                                                     :label (entity/display-string entity)})))
             :search-visible-threshold 10}
            [button/sm-secondary-solid
             {:size :small}
             [:i.fal.fa-plus]
             (loc/tr :add-group)]])
         [combo-box/view
          {:on-change                (fn [entity-id]
                                       (rf/dispatch [::event/add-decision-participants [entity-id]]))
           :options                  (->> @session/persons
                                          (concat (session/active-accounts nil))
                                          (remove (fn [{:keys [db/id]}]
                                                    (some #{id} participant-persons)))
                                          (mapv (fn [entity]
                                                  {:value (:db/id entity)
                                                   :label (entity/display-string entity)})))
           :search-visible-threshold 10}
          [button/sm-secondary-solid
           {:size :small}
           [:i.fal.fa-plus]
           (loc/tr :add-people)]]]
        (when (seq participants)
          [:div
           {:class "py-3"}
           [:table
            {:class "w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
            [:thead
             [:tr
              [:th
               {:class          "py-2 px-3 w-[24%] bg-base-regular"
                :data-title-col true}
               (loc/tr :name)]
              [:th
               {:class           "py-2 px-3 w-[24%] bg-base-regular"
                :data-select-col true}
               (loc/tr :attendance)]
              [:th
               {:class           "py-2 px-3 w-[24%] bg-base-regular"
                :data-select-col true}
               (loc/tr :role)]
              [:th
               {:class          "py-2 px-3 w-[24%] bg-base-regular"
                :data-notes-col true}
               (loc/tr :notes)]
              [:th
               {:class         "py-2 px-3 w-[4%] bg-base-regular"
                :data-icon-col true}
               ""]]]
            [:tbody
             (->>
              participants
              (map
               (fn [participant]
                 (let [participant-id (:db/id participant)
                       person-id (:decision-participant/person participant)
                       person (session/get-entity person-id)
                       person-name (entity/display-string person)
                       account? (-> person
                                    :entity/type
                                    (= :entity.type/account))
                       deactivated-account? (when account?
                                              (not (session/active-account-id person-id)))
                       attendance (:decision-participant/attendance participant)
                       notes (:decision-participant/notes participant)
                       validation-errors (get validation-errors participant-id)]
                   ^{:key (str "participant-" participant-id)}
                   [:tr
                    {:class "*:align-middle"}
                    [:td
                     {:class "py-2 px-3 w-[24%] align-middle text-semibold"}
                     [:span person-name]
                     (when deactivated-account?
                       [:span
                        {:class "font-bold"}
                        (str " (" (loc/tr :deactivated-account) ")")])]
                    [:td
                     {:class "py-2 px-3 w-[24%]"}
                     [data-field
                      {:editor-type :editor.type/select
                       :data-type   :data.type/keyword
                       :attribute   :decision-participant/attendance
                       :value       attendance
                       :options     (->> (registry/get-field-schema tenant-schema :decision-participant/attendance)
                                         :options
                                         (mapv (fn [{:keys [key value label]}]
                                                 {:value (or value key) :label label})))
                       :error-text  (get validation-errors :decision-participant/attendance)
                       :on-save!    (fn [attr value]
                                      (update-entity-field participant-id attr value))}]]
                    [:td
                     {:class "py-2 px-3 w-[24%]"}
                     [participant-role-field
                      {:role-ids   (:decision-participant/role participant)
                       :error-text (get validation-errors :decision-participant/role)
                       :on-save!   (fn [role-ids]
                                     (update-entity-field participant-id
                                                          :decision-participant/role
                                                          role-ids))}]]
                    [:td
                     {:class "py-2 px-3 w-[24%]"}
                     [data-field
                      {:data-type :data.type/text-multiline
                       :attribute :decision-participant/notes
                       :value     notes
                       :on-save!  (fn [attr value]
                                    (update-entity-field participant-id attr value))}]]
                    [:td
                     {:class "py-2 px-3 w-[4%] align-middle"}
                     [button/square-secondary-open
                      {:tooltip    (loc/tr :decisions/remove-participant)
                       :aria-label (loc/tr :decisions/remove-participant)
                       :on-click   #(rf/dispatch [::event/remove-decision-participant participant-id])}
                      [:i.fal.fa-trash-alt]]]])))
              (doall))]]])]])))

(defn investment-reservations-editor
  [{:keys [decision entities update-entity-field tenant-schema]}]
  (let [current-reservations (fn []
                               (->> decision
                                    :decision/investment-reservations
                                    (mapv (partial get entities))))
        investment-reservations (current-reservations)
        table-enabled-fields
        (->> investment-reservations
             (mapcat (fn [reservation]
                       (-> reservation
                           :investment-reservation/type
                           (session/get-entity)
                           :investment-reservation-type/enabled-fields)))
             (into #{}))
        all-table-fields [:investment-reservation/amount
                          :investment-reservation/amount--sym
                          :investment-reservation/expiry-date
                          :investment-reservation/expiry-condition
                          :investment-reservation/void-date
                          :investment-reservation/voided-by
                          :investment-reservation/notes]
        table-visible-fields
        (->> all-table-fields
             (keep (fn [field]
                     (let [field-schema (registry/get-field-schema tenant-schema field)
                           label (attribute/attribute-label tenant-schema field)
                           related-field (:field/related-fields field-schema)
                           enabled? (if related-field
                                      (some #{related-field} table-enabled-fields)
                                      (some #{field} table-enabled-fields))]
                       (when enabled?
                         {:field        field
                          :field-schema field-schema
                          :label        label}))))
             (vec))]
    [:div
     {:class "p-4 flex flex-col gap-3 border border-solid border-base-light-border-paler rounded"}
     [:h4
      {:class "pb-2 font-bold text-xl"}
      (loc/tr :investment-reservations)]
     [:div
      [:div
       {:class "flex gap-2"}
       [combo-box/view
        {:options                  (->> @session/investment-reservation-types
                                        (remove :common/archived?)
                                        (sort-by :common/ord)
                                        (mapv (fn [entity]
                                                {:value (:db/id entity)
                                                 :label (entity/display-string entity)})))
         :on-change                (fn [reservation-type-id]
                                     (rf/dispatch [::event/add-investment-reservation reservation-type-id]))
         :search-visible-threshold 10}
        [button/sm-secondary-solid
         {}
         [:i.fal.fa-plus]
         (loc/tr :add-reservation)]]]
      (when (seq investment-reservations)
        [:div
         {:class "pt-2"}
         [:table
          {:class "w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
          [:thead
           [:tr
            [:th
             {:class "py-2 px-3 bg-base-regular"}
             (loc/tr :type)]
            (->> table-visible-fields
                 (map (fn [{:keys [field label]}]
                        ^{:key (str "table-header-" field)}
                        [:th
                         {:class "py-2 px-3 w-[20%] bg-base-regular"}
                         label])))
            [:th
             {:class "py-2 px-3 bg-base-regular"}
             (loc/tr :subtract-from-dry-powder)]
            [:th
             {:class         "py-2 px-3 bg-base-regular"
              :data-icon-col true}]]]
          [:tbody
           (->>
            investment-reservations
            (map
             (fn [investment-reservation]
               (let [id (:db/id investment-reservation)
                     investment-reservation-type (-> investment-reservation
                                                     :investment-reservation/type
                                                     (session/get-entity))
                     {:keys [investment-reservation-type/title
                             investment-reservation-type/enabled-fields
                             investment-reservation-type/subtract-from-dry-powder]}
                     investment-reservation-type]
                 ^{:key (str "investment-reservation-" id)}
                 [:tr
                  {:class "*:align-middle"}
                  [:td
                   {:class "py-2 px-3"}
                   title]
                  (->> table-visible-fields
                       (map
                        (fn [{:keys [field field-schema]}]
                          (let [enabled? (if (:field/secondary-field? field-schema)
                                           (->> field-schema
                                                :field/related-fields
                                                (contains? enabled-fields))
                                           (some #{field} enabled-fields))
                                value (get investment-reservation field)]
                            ^{:key (str id "-field-" field)}
                            [:td
                             {:class "py-2 px-3"}
                             (if enabled?
                               [schema-field
                                (merge
                                 field-schema
                                 {:placeholder       ""
                                  :value             value
                                  :entity-type       (:entity/type investment-reservation)
                                  :containing-entity investment-reservation
                                  :on-save!          (fn [new-value]
                                                       (update-entity-field id field new-value))})]
                               "")]))))
                  [:td
                   {:class         "py-2 px-3"
                    :data-icon-col true}
                   (if subtract-from-dry-powder
                     [:i.fas.fa-check-circle
                      {:class "text-success-light-text"}]
                     [:i.fas.fa-times-circle
                      {:class "text-danger-light-text"}])]
                  [:td
                   {:class           "py-2 px-3"
                    :data-icon-col   true
                    :data-narrow-col true}
                   [button/square-secondary-open
                    {:tooltip    (loc/tr :decisions/remove-reservation)
                     :aria-label (loc/tr :decisions/remove-reservation)
                     :on-click   #(rf/dispatch [::event/remove-investment-reservation id])}
                    [:i.fal.fa-trash-alt]]]])))
            (doall))]]])]]))

(defn decision-conditions-editor
  [{:keys [_decision _entities _update-entity-field _validation-errors _tenant-schema]}]
  (r/create-class
   {:reagent-render
    (fn [{:keys [decision entities update-entity-field
                 validation-errors tenant-schema]}]
      (let [decision-id (:db/id decision)
            focus-condition-id @(rf/subscribe [::sub/focused-condition-id])
            condition-entity-ids (:decision/conditions decision)
            condition-entities (mapv (partial get entities) condition-entity-ids)]
        [:div
         {:class "p-4 flex flex-col gap-3 border border-solid border-base-light-border-paler rounded"}
         [:h4
          {:class "font-bold text-xl"}
          (loc/tr :conditions)]
         [:div
          [button/sm-secondary-solid
           {:size     :small
            :on-click #(rf/dispatch [::event/add-decision-condition decision-id])}
           [:i.fal.fa-plus]
           (loc/tr :add-condition)]]
         [:div
          {:class "py-2"}
          (when (seq condition-entities)
            [:div
             {:class ""}
             [:table
              {:class "w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
              [:thead
               [:tr
                [:th
                 {:class          "py-2 px-3 w-[32%] bg-base-regular"
                  :data-title-col true}
                 (loc/tr :condition)]
                [:th
                 {:class           "py-2 px-3 w-[32%] bg-base-regular"
                  :data-select-col true}
                 (loc/tr :status)]
                [:th
                 {:class          "py-2 px-3 w-[32%] bg-base-regular"
                  :data-notes-col true}
                 (loc/tr :description)]
                [:th
                 {:class         "py-2 px-3 w-[4%] bg-base-regular"
                  :data-icon-col true}]]]
              [:tbody
               (->>
                condition-entities
                (map
                 (fn [condition]
                   (let [id (:db/id condition)
                         title (:decision-condition/title condition)
                         status (:decision-condition/status condition)
                         description (:decision-condition/description condition)]
                     ^{:key (str "decision-condition-" id)}
                     [:tr
                      {:class "even:bg-primary-light *:align-middle"}
                      [:td
                       {:ref   (fn [ref]
                                 (when (and ref (= id focus-condition-id))
                                   (when-let [input (oops/ocall ref "querySelector" "input")]
                                     (.focus input)
                                     (rf/dispatch [::event/set-focused-condition-id nil]))))
                        :class "py-2 px-3 w-[32%]"}
                       [data-field
                        {:data-type   :data.type/text-simple
                         :attribute   :decision-condition/title
                         :placeholder (loc/tr :condition-title-placeholder)
                         :error-text  (get-in validation-errors [id :decision-condition/title])
                         :value       title
                         :on-save!    (fn [attr value]
                                        (update-entity-field id attr value))}]]
                      [:td
                       {:class "py-2 px-3 w-[32%]"}
                       [data-field
                        {:editor-type        :editor.type/select
                         :data-type          :data.type/keyword
                         :attribute          :decision-condition/status
                         :menu-portal-target (dg/element-by-id "react-select-menu-container")
                         :placeholder        (loc/tr :condition-status-placeholder)
                         :error-text         (get-in validation-errors [id :decision-condition/status])
                         :value              status
                         :options            (->> (registry/get-field-schema tenant-schema :decision-condition/status)
                                                  :options
                                                  (mapv (fn [{:keys [key value label]}]
                                                          {:value (or value key) :label label})))
                         :on-save!           (fn [attr value]
                                               (update-entity-field id attr value))}]]
                      [:td
                       {:class "py-2 px-3 w-[32%]"}
                       [data-field
                        {:data-type   :data.type/text-multiline
                         :attribute   :decision-condition/description
                         :placeholder (loc/tr :condition-desc-placeholder)
                         :value       description
                         :on-save!    (fn [attr value]
                                        (update-entity-field id attr value))}]]
                      [:td
                       {:class           "py-2 px-3 w-[6%] align-middle"
                        :data-narrow-col true}
                       [button/square-secondary-open
                        {:tooltip    (loc/tr :decisions/remove-condition)
                         :aria-label (loc/tr :decisions/remove-condition)
                         :on-click   #(rf/dispatch [::event/remove-decision-condition id])}
                        [:i.fal.fa-trash-alt]]]])))
                (doall))]]])]]))}))

(defn decision-details-editor
  [{:keys [title fields decision validation-errors update-entity-field tenant-schema hide-notes-column?]}]
  (let [decision-id (:db/id decision)]
    [:div
     {:class "p-4 flex flex-col gap-3 border border-solid border-base-light-border-paler rounded"}
     [:h4
      {:class "pb-2 font-bold text-xl"}
      (or title (loc/tr :decision-details))]
     [:table
      {:class "w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
      [:thead
       [:tr
        [:th
         {:class          "py-2 px-3 w-4/12 bg-base-regular"
          :data-title-col true}
         (loc/tr :field)]
        [:th
         {:class           "py-2 px-3 w-4/12 bg-base-regular"
          :data-select-col true}
         (loc/tr :value)]
        (when-not hide-notes-column?
          [:th
           {:class          "py-2 px-3 w-4/12 bg-base-regular"
            :data-notes-col true}
           (loc/tr :notes)])]]
      [:tbody
       (->> fields
            (map
             (fn [field]
               (let [field-schema (registry/get-field-schema tenant-schema field)
                     field-label (loc/field-name field field-schema)
                     notes-field (keyword (subs (str field "-notes") 1))
                     notes-field-schema (registry/get-field-schema tenant-schema notes-field)]
                 ^{:key (str "field-" field)}
                 [:tr
                  {:class "even:bg-primary-light *:align-middle"}
                  [:td
                   {:class "py-2 px-3 w-4/12 align-middle font-semibold"}
                   field-label]
                  [:td
                   {:class "py-2 px-3 w-4/12"}
                   [schema-field
                    (merge
                     field-schema
                     {:placeholder       (loc/tr :select)
                      :value             (get decision field)
                      :error-text        (get-in validation-errors [decision-id field])
                      :entity-type       (:entity/type decision)
                      :containing-entity decision
                      :on-save!          (fn [new-value]
                                           (update-entity-field decision-id field new-value))})]]
                  (when-not hide-notes-column?
                    [:td
                     {:class "py-2 px-3 w-4/12"}
                     (when notes-field-schema
                       [data-field
                        {:data-type :data.type/text-multiline
                         :attribute notes-field
                         :value     (get decision notes-field)
                         :on-save!  (fn [attr value]
                                      (update-entity-field decision-id attr value))}])])])))
            (doall))]]]))

#_(defn decision-notes-and-attachments
  [{:keys [decision update-entity-field]}]
  (let [decision-id (:db/id decision)]
    [:div
     [:h4
      {:class "font-bold text-xl"}
      (loc/tr :notes)]
     [:div
      {:class "mt-3"}
      [:div
       {:class "max-w-[500px] min-w-[200px] h-[210px] *:h-full *:flex *:flex-col"}
       [rich-text/editor
        {:value     (:decision/notes decision)
         :on-change (fn [value]
                      (update-entity-field decision-id :decision/notes value))
         :border?   true}]]]]))

(defn decision-notes-and-attachments
  [{:keys [decision update-entity-field]}]
  (let [decision-id (:db/id decision)]
    [:div
     [:h4
      {:class "font-bold text-xl"}
      (loc/tr :notes)]
     [:div
      {:class "mt-3"}
      [:div
       {:class "max-w-[500px] min-w-[200px] h-[210px]"}
       [tiptap/editor
        {:value (:decision/notes decision)
         :on-change (fn [value]
                      (update-entity-field decision-id :decision/notes value))
         :class "h-full flex flex-col"
         :editor-class "h-full overflow-y-auto"
         :inline-options [:bold :italic :underline]}]]]]))

(defn ic-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/fund-has-sufficient-capacity
               :decision/complies-with-investment-strategy
               :decision/ic-approves-proposed-investment]})]
   [decision-participant-editor editor-props]
   [investment-reservations-editor editor-props]
   [decision-conditions-editor editor-props]
   [decision-notes-and-attachments editor-props]])

(defn ic-exit-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/ic-approves-proposed-exit]})]
   [decision-participant-editor editor-props]
   [decision-conditions-editor editor-props]
   [decision-notes-and-attachments editor-props]])

(defn aifm-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/deciding-party
               :decision/portfolio-manager
               :decision/portfolio-manager-approves-proposed-investment
               :decision/pre-investment-checks-completed
               :decision/ic-recommended-to-fund
               :decision/fund-has-sufficient-capacity
               :decision/related-to-holding]})]
   [decision-participant-editor editor-props]
   [investment-reservations-editor editor-props]
   [decision-conditions-editor editor-props]
   [decision-details-editor
    (merge
     editor-props
     {:title  (loc/tr :approvals-needed)
      :fields [:decision/board-approval-needed
               :decision/advisory-board-approval-needed]})]])

(defn aifm-exit-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/deciding-party
               :decision/portfolio-manager
               :decision/portfolio-manager-approves-proposed-exit
               :decision/pre-exit-checks-completed
               :decision/ic-recommended-the-exit
               :decision/fund-has-sufficient-capacity]})]
   [decision-participant-editor editor-props]
   [decision-conditions-editor editor-props]
   [decision-details-editor
    (merge
     editor-props
     {:title  (loc/tr :approvals-needed)
      :fields [:decision/advisory-board-approval-needed]})]])

(defn one-pager-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/approval-status]
      :hide-notes-column? true})]
   [decision-notes-and-attachments editor-props]])

(defn icm-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/approval-status]
      :hide-notes-column? true})]
   [decision-notes-and-attachments editor-props]])

(defn scm-decision-editor
  [editor-props]
  [:div
   {:class "flex flex-col gap-4 pb-8"}
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date
               :decision/approval-status]
      :hide-notes-column? true})]
   [decision-notes-and-attachments editor-props]])

(defn advisory-board-decision-editor
  [editor-props]
  [:<>
   [decision-details-editor
    (merge
     editor-props
     {:fields [:decision/date :decision/meeting-type]})]
   [decision-participant-editor editor-props]])

(defn footer
  [{:keys [modal-type modified? decision-id close-modal! submit-success!]}]
  (let [submit-label (case modal-type
                       :create-modal (loc/tr :create-decision)
                       :editor-modal (loc/tr :update- [(loc/tr :decision)])
                       nil)
        submit-action (case modal-type
                        :create-modal ::event/create-decision
                        :editor-modal ::event/update-decision
                        nil)]
    [:<>
     [modal/left-cancel-only close-modal! nil]
     [:div
      {:class "flex items-center gap-3 justify-end"}
      (when-not (string? decision-id)
        [button/danger-solid
         {:on-click #(rf/dispatch [::event/set-delete-confirmation-modal-open close-modal!])}
         (loc/tr :delete-entity [(loc/tr :decision)])])
      [button/primary-solid
       {:disabled? (and (= modal-type :editor-modal)
                        (not modified?))
        :on-click  #(rf/dispatch [submit-action submit-success!])}
       submit-label]]]))


(defn delete-confirmation-modal
  [{:keys [decision-entity-type submit-success!]}]
  (let [open? @(rf/subscribe [::sub/delete-confirmation-modal?])
        decision-type-name (loc/tr :decision-entity-type)]
    [modal/view
     {:visible?      open?
      :hide!         #(rf/dispatch [::event/cancel-delete-decision])
      :backdrop      "static"
      :close-button? true}
     [:h4 (str (loc/tr :delete-entity [decision-type-name]) "?")]
     [:p.mb-3.mt-2 (loc/tr :this-action-cannot-be-undone)]
     [:div
      {:class "w-full flex gap-4 justify-between"}
      [button/secondary-hollow
       {:on-click #(rf/dispatch [::event/cancel-delete-decision])}
       (loc/tr :cancel)]
      [button/danger-solid
       {:on-click #(rf/dispatch [::event/delete-decision submit-success!])}
       "Delete decision"]]]))

(defn view
  [{:keys [props]}]
  (let [{:keys [decision-id submit-success-cb-event]} props]
    (fn [{:keys [shown? close-modal! topmost?]}]
      (let [close-modal! (fn []
                           (rf/dispatch [::event/cleanup])
                           (close-modal!))
            submit-success! (fn []
                              ;; HACK
                              (when (vector? submit-success-cb-event)
                                (rf/dispatch submit-success-cb-event))
                              (close-modal!))
            entities @(rf/subscribe [::sub/entities])
            validation-errors @(rf/subscribe [::sub/validation-errors])
            modal-type @(rf/subscribe [::sub/modal-type])
            modified? @(rf/subscribe [::sub/modified?])
            tenant-schema @session/tenant-schema
            update-entity-field (fn [entity-id field value]
                                  (rf/dispatch [::event/update-entity-field
                                                entity-id field value]))
            decision (get entities decision-id)
            decision-entity-type (:entity/type decision)
            editor-props {:decision            decision
                          :entities            entities
                          :validation-errors   validation-errors
                          :tenant-schema       tenant-schema
                          :update-entity-field update-entity-field}]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :size          ::modal-style/full
          :close-button? true}
         (loc/tr decision-entity-type)
         [:<>
          [delete-confirmation-modal {:decision-entity-type decision-entity-type
                                      :submit-success!      submit-success!}]
          (case decision-entity-type
            :entity.type/ic-decision [ic-decision-editor editor-props]
            :entity.type/ic-exit-decision [ic-exit-decision-editor editor-props]
            :entity.type/aifm-decision [aifm-decision-editor editor-props]
            :entity.type/aifm-exit-decision [aifm-exit-decision-editor editor-props]
            :entity.type/advisory-board-decision [advisory-board-decision-editor editor-props]
            :entity.type/one-pager-decision [one-pager-decision-editor editor-props]
            :entity.type/icm-decision [icm-decision-editor editor-props]
            :entity.type/scm-decision [scm-decision-editor editor-props]
            [:<>])]
         [footer
          {:modal-type      modal-type
           :decision-id     decision-id
           :modified?       modified?
           :submit-success! submit-success!
           :close-modal!    close-modal!}]]))))
